import { t } from './markets'

// Bookable price tiles for the UAE / Malaysia aircon pages.
//
// Each tile carries the requestCriteria the booking flow sends to the backend.
// The criteria VALUES are the internal keys util/AirconCostCalculator.java
// parses ("General servicing for cleaning", "Chemical cleaning", mount
// "Wall"/"Ducted"/"Ceiling") and must not be localised - only the human
// `criteria` text next to them is, because ops and the customer read that.
//
// prices come from js/aircon-cost buildAirconPriceTables(countryCd).

// Market non-wall unit -> backend mount key. The UAE's common non-wall unit is
// the concealed ducted split; Malaysia's is the ceiling cassette.
export const nonWallMountKey = (market) => (market.countryCd === 'ae' ? 'Ducted' : 'Ceiling')
export const nonWallMountLabel = (market) =>
    market.countryCd === 'ae' ? t(market, 'mountDucted') : t(market, 'mountCeiling')

const unitWord = (market, n) => `${t(market, 'acUnit')}${n > 1 ? 's' : ''}`

// Price tile labels say "AC units" in every market, as on the Singapore cards
// ("AC" is understood in the UAE and Malaysia alike). Criteria and summaries
// keep the market term (unitWord) - ops reads those.
const tileUnitWord = (n) => `AC unit${n > 1 ? 's' : ''}`

// Tile label naming the non-wall unit type, so a ducted / cassette price is
// never read as the wall-unit price: "2 ducted units", "1 cassette unit".
const nonWallShort = (market) => (market.countryCd === 'ae' ? 'ducted' : 'cassette')
const servicingLabel = (market, n, mountKey) =>
    mountKey === 'Wall' ? `${n} ${tileUnitWord(n)}` : `${n} ${nonWallShort(market)} unit${n > 1 ? 's' : ''}`

const mountCriteria = (market, mountKey) => ({
    24: mountKey,
    criteria: `Mount type - ${mountKey === 'Wall' ? t(market, 'mountWall') : nonWallMountLabel(market)}`,
})

const unitsCriteria = (market, n) => ({
    2: String(n),
    criteria: `Number of ${t(market, 'acUnit')}s to be serviced - ${n}`,
})

const ONCE = { 21: 'Only once', criteria: 'Frequency of service - Only once' }

// service: 'general' | 'chemicalWash' | 'chemicalOverhaul'
export const servicingTiles = (market, tables, service, mountKey = 'Wall') => {
    const rows = service === 'general' ? tables.general
        : service === 'chemicalWash' ? tables.chemicalWash
        : tables.chemicalOverhaul
    const label = service === 'general' ? t(market, 'acGeneral')
        : service === 'chemicalWash' ? t(market, 'acChemicalWash')
        : t(market, 'acChemicalOverhaul')

    return rows.map((row) => {
        const n = row.units
        const typeCriteria = service === 'general'
            ? [{ 1: 'General servicing for cleaning', criteria: `Type of service - ${label}` }]
            : [
                { 1: 'Chemical cleaning', criteria: `Type of service - ${label}` },
                service === 'chemicalWash'
                    ? { 3: 'Basic chemical wash (partial dismantling may be needed)', criteria: 'Basic chemical wash (partial dismantling may be needed)' }
                    : { 3: 'Chemical overhaul (deeper cleaning by dismantling)', criteria: 'Chemical overhaul (deeper cleaning by dismantling)' },
            ]
        return {
            key: `${service}-${mountKey}-${n}`,
            units: n,
            label: servicingLabel(market, n, mountKey),
            price: mountKey === 'Wall' ? row.wall : row.ducted,
            summary: `${label} - ${n} ${mountKey === 'Wall' ? t(market, 'mountWall') : nonWallMountLabel(market)} ${unitWord(market, n)}`,
            criteria: [...typeCriteria, unitsCriteria(market, n), ONCE, mountCriteria(market, mountKey)],
        }
    })
}

// times: 3 | 4 visits a year, wall units
export const annualTiles = (market, tables, times) =>
    tables.annual.map((row) => {
        const n = row.units
        return {
            key: `annual-${times}-${n}`,
            units: n,
            label: `${n} ${tileUnitWord(n)}`,
            price: times === 3 ? row.threeTimes : row.fourTimes,
            summary: `${t(market, 'acAnnual')} - ${times} visits a year, ${n} ${unitWord(market, n)}`,
            criteria: [
                { 1: 'General servicing for cleaning', criteria: `Type of service - ${t(market, 'acGeneral')}` },
                unitsCriteria(market, n),
                { 21: 'Annual package', criteria: 'Frequency of service - Annual package' },
                { 23: `${times} times/year`, criteria: `Visits per year - ${times}` },
                mountCriteria(market, 'Wall'),
            ],
        }
    })

// half: true = top-up (charged at the range midpoint), false = full refill
export const gasTiles = (market, tables, refrigerant = 'R410A', half = true, max = 3) =>
    [1, 2, 3].slice(0, max).map((n) => {
        const each = half ? tables.gas[refrigerant].half : tables.gas[refrigerant].full
        const quantity = half ? 'Half tank refill' : 'Full tank refill'
        return {
            key: `gas-${refrigerant}-${half ? 'half' : 'full'}-${n}`,
            units: n,
            label: `${n} ${n > 1 ? 'outdoor units' : 'outdoor unit'}`,
            price: Math.round(each * n * 100) / 100,
            summary: `${t(market, 'acGas')} (${refrigerant}, ${half ? 'top-up' : 'full refill'}) - ${n} outdoor unit${n > 1 ? 's' : ''}`,
            criteria: [
                { 1: 'Gas top up only', criteria: `Type of service - ${t(market, 'acGas')}` },
                { 27: String(n), criteria: `Number of outdoor units for gas refill - ${n}` },
                { 22: quantity, criteria: `Quantity of gas for refill - ${quantity}` },
                { 26: refrigerant, criteria: `Gas type - ${refrigerant}` },
            ],
        }
    })

export const repairTile = (market, tables, problem = 'Not sure') => ({
    key: `repair-${problem}`,
    label: 'Diagnosis visit',
    price: tables.repairDiagnosis,
    summary: `${t(market, 'acRepair')} - ${problem}`,
    criteria: [
        { 1: 'Aircon repair (water leaking, not cooling, no power etc.)', criteria: `Type of service - ${t(market, 'acRepair')}` },
        { 4: problem, criteria: `Problem - ${problem}` },
    ],
})

export const drainFlushTiles = (market, tables, max = 3) =>
    tables.drainFlush.slice(0, max).map((row) => ({
        key: `flush-${row.units}`,
        units: row.units,
        label: `${row.units} ${tileUnitWord(row.units)}`,
        price: row.price,
        summary: `Drain pipe flush - ${row.units} ${unitWord(market, row.units)}`,
        criteria: [
            { 1: 'Drain pipe flush', criteria: 'Type of service - Drain pipe flush' },
            unitsCriteria(market, row.units),
        ],
    }))

// The "deals" block on the city hubs and locality pages - the counterpart of
// Singapore's AirconDealItem rows: repair problems, then one row per service.
// Split (wall) units here; the hero card above already shows ducted prices
// for the UAE. Each tile carries the criteria for MarketBooking.book().
export const buildMarketDeals = (market, tables) => {
    const refrigerant = market.countryCd === 'my' ? 'R32' : 'R410A'
    return {
        repair: {
            title: `Book ${t(market, 'acRepair')}`,
            problems: [
                { emoji: '💧', label: t(market, 'acLeaking'), tile: repairTile(market, tables, 'water leaking') },
                { emoji: '🌡️', label: t(market, 'acNotCooling'), tile: repairTile(market, tables, 'not cooling') },
            ],
        },
        groups: [
            { key: 'general', title: t(market, 'acGeneral'), jobScopeKey: 'general', subtitle: t(market, 'mountWall'), moreOptionsLabel: t(market, 'acAnnual'), tiles: servicingTiles(market, tables, 'general', 'Wall') },
            { key: 'chemicalWash', title: t(market, 'acChemicalWash'), jobScopeKey: 'chemicalWash', subtitle: t(market, 'mountWall'), moreOptionsLabel: 'More units, other mount types', tiles: servicingTiles(market, tables, 'chemicalWash', 'Wall') },
            { key: 'chemicalOverhaul', title: t(market, 'acChemicalOverhaul'), jobScopeKey: 'chemicalOverhaul', subtitle: t(market, 'mountWall'), moreOptionsLabel: 'More units, other mount types', tiles: servicingTiles(market, tables, 'chemicalOverhaul', 'Wall') },
            { key: 'gas', title: t(market, 'acGas'), jobScopeKey: 'gasRefillHalf', subtitle: `${refrigerant} top-up`, moreOptionsLabel: 'Full refill and other gases', tiles: gasTiles(market, tables, refrigerant, true) },
        ],
    }
}

// Two-line tab labels for the hero booking card (ServiceTypeTabs renders
// label[0] <br/> label[1]). Short forms of the market terms in utils/markets.js.
const HERO_TAB_LABELS = {
    ae: { general: ['AC', 'Cleaning'], chemicalWash: ['Coil', 'Cleaning'], chemicalOverhaul: ['Deep', 'Cleaning'] },
    my: { general: ['General', 'Service'], chemicalWash: ['Chemical', 'Wash'], chemicalOverhaul: ['Chemical', 'Overhaul'] },
}

/**
 * Tabs and price tiles for the SG-style hero card (components/market/MarketAirconHero),
 * keyed by the page's content.panel. Tab `value`s reuse the AirconBookingLayout
 * theme keys (general / chemicalWash / chemicalOverhaul / gasRefill /
 * gasRefillHalf) so the colours match the Singapore cards.
 *
 * Returns [{ value, label: [line1, line2], ariaLabel, tiles }].
 */
export const heroGroups = (market, tables, panel = {}) => {
    const labels = HERO_TAB_LABELS[market.countryCd] ?? HERO_TAB_LABELS.my
    const term = t(market, 'acShort')
    const mount = panel.defaultMount || (market.countryCd === 'ae' ? nonWallMountKey(market) : 'Wall')

    switch (panel.type) {
        case 'gas':
            return [
                { value: 'gasRefillHalf', label: ['Gas', market.countryCd === 'ae' ? 'Refill' : 'Top Up'], ariaLabel: `Select ${term} gas top-up`, tiles: gasTiles(market, tables, market.countryCd === 'my' ? 'R32' : 'R410A', true) },
                { value: 'gasRefill', label: ['Full', 'Refill'], ariaLabel: `Select full ${term} gas refill`, tiles: gasTiles(market, tables, market.countryCd === 'my' ? 'R32' : 'R410A', false) },
            ]
        case 'annual':
            return [
                { value: 'general', label: ['3 Visits', 'a Year'], ariaLabel: 'Select 3 visits a year', tiles: annualTiles(market, tables, 3) },
                { value: 'chemicalWash', label: ['4 Visits', 'a Year'], ariaLabel: 'Select 4 visits a year', tiles: annualTiles(market, tables, 4) },
            ]
        case 'drainFlush':
            return [{ value: 'general', label: ['Drain', 'Flush'], ariaLabel: 'Drain pipe flush', tiles: drainFlushTiles(market, tables) }]
        case 'repair':
            return [{ value: 'general', label: ['Repair', 'Visit'], ariaLabel: `${t(market, 'acRepair')} diagnosis`, tiles: [repairTile(market, tables, panel.problems?.[0] ?? 'Not sure')] }]
        default: {
            const services = panel.type === 'servicing' && panel.services?.length ? panel.services : ['general', 'chemicalWash', 'chemicalOverhaul']
            return services.map((service) => ({
                value: service,
                label: labels[service],
                ariaLabel: `Select ${labels[service].join(' ')}`,
                tiles: servicingTiles(market, tables, service, mount),
            }))
        }
    }
}
